// Generates a property PDF and downloads it. The `template` selects which
// report to render. We call it through a Supabase Edge Function proxy — the
// underlying disco-outpost cloud function sends no CORS headers, so the browser
// can't reach it directly (see supabase/functions/create-property-report).

import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/shared/integrations/supabase/client";

export type PropertyReportTemplate =
  | "property_category_summary"
  | "property_summary_report"
  | "property_report";

/** Dropdown options — value is the endpoint's `template`, label is for humans. */
export const PROPERTY_REPORT_TEMPLATES: ReadonlyArray<{
  value: PropertyReportTemplate;
  label: string;
}> = [
  { value: "property_category_summary", label: "Property Category Summary" },
  { value: "property_summary_report", label: "Property Summary Report" },
  { value: "property_report", label: "Property Report" },
];

const ENDPOINT = `${SUPABASE_URL}/functions/v1/create-property-report`;

export async function generatePropertyReport(
  propertyId: string,
  template: PropertyReportTemplate,
  filenameBase?: string
): Promise<void> {
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify({ property_id: propertyId, template }),
  });

  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new Error(
      `Report generation failed (${res.status})${detail ? `: ${detail.slice(0, 200)}` : ""}`
    );
  }

  const fallbackName = `${filenameBase?.trim() || template}.pdf`;

  // The endpoint returns JSON: { message, download_url, file_name, document_id }
  // — the PDF itself lives in Firebase Storage. Direct-PDF kept as a fallback.
  const contentType = res.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const json = (await res.json()) as Record<string, unknown>;
    const link =
      (json.url ?? json.download_url ?? json.pdf_url ?? json.file_path) as
        | string
        | undefined;
    if (!link) throw new Error("Report endpoint returned JSON without a download URL");
    const fileRes = await fetch(link);
    if (!fileRes.ok) throw new Error(`Failed to download report (${fileRes.status})`);
    const name = typeof json.file_name === "string" && json.file_name ? json.file_name : fallbackName;
    downloadBlob(await fileRes.blob(), name);
    return;
  }

  downloadBlob(await res.blob(), fallbackName);
}

function downloadBlob(blob: Blob, fileName: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}
